import * as React from 'react';
import { FC, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useRoutes from 'hooks/use-routes';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { isMobile } from 'utils/environment';
import { milesBetweenCoordinates, readableDistanceFromStore } from 'utils/restaurant';

import { ModalConfirmRestaurantDesktopView } from './modal-confirm-restaurant-desktop.view';
import { ModalConfirmRestaurantView } from './modal-confirm-restaurant.view';
import { IModalConfirmRestaurantProps } from './types';

/**
 *
 * Prompts user to confirm or change auto-determined nearest restaurant
 *
 */
const ModalConfirmRestaurant: FC<IModalConfirmRestaurantProps> = ({
  confirmRestaurant,
  restaurant
}) => {
  const { region } = useLocale();
  const { formatMessage } = useIntl();
  const { userCoordinates } = useGeolocation();
  const { setServiceMode } = useServiceModeContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const { location } = useLocationContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const storeLocatorRoute = getLocalizedRouteForPath('store-locator');
  const navigate = useNavigate();

  const handleConfirmRestaurant = (confirmed: boolean) => confirmRestaurant(confirmed);

  const handleChangeRestaurant = () => {
    // if ordering not enabled, set default to takeout
    if (!enableOrdering) {
      setServiceMode(ServiceMode.TAKEOUT);
    }
    navigate(storeLocatorRoute);
    handleConfirmRestaurant(false);
  };

  const restaurantData = useMemo(() => {
    const { city, stateProvince, address1 } = restaurant?.physicalAddress || {};
    const { latitude: lat, longitude: lng } = restaurant || {};
    const distance = userCoordinates
      ? milesBetweenCoordinates(userCoordinates, {
          lat,
          lng
        })
      : 0;
    const distanceText = readableDistanceFromStore(distance, region, formatMessage);
    return { city, stateProvince, address: address1, lat, lng, distanceText };
  }, [formatMessage, region, restaurant, userCoordinates]);

  const text = {
    changeText: formatMessage({ id: 'change' }),
    confirmText: formatMessage({ id: 'confirm' }),
    yourClosestStore: formatMessage({ id: 'yourClosestStore' })
  };

  const ConfirmRestaurantModal = isMobile()
    ? ModalConfirmRestaurantView
    : ModalConfirmRestaurantDesktopView;

  return !location.pathname.includes(storeLocatorRoute) ? (
    <ConfirmRestaurantModal
      onDismiss={handleConfirmRestaurant}
      handleConfirmRestaurant={handleConfirmRestaurant}
      handleChangeRestaurant={handleChangeRestaurant}
      {...restaurantData}
      {...text}
    />
  ) : null;
};

export default ModalConfirmRestaurant;
